import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronDown, LogOut, Menu } from 'lucide-react'
import { BrandLockup } from '@/components/BrandLogo'
import { ThemeToggle } from '@/components/ThemeToggle'
import { SuiteSwitcher } from '@/components/landing/SuiteSwitcher'
import { useAuth } from '@/store/auth'

/**
 * The signed-in masthead.
 *
 * <p>Built the same way as the marketing header — translucent surface over a
 * blur on a hairline rule — so crossing from the site into the application
 * does not feel like arriving at a different product. It carries the suite
 * switcher and the account menu; the sidebar beside it carries navigation.
 */
const TITLES: Record<string, string> = {
  '/chat': 'Chat',
  '/models': 'Models',
  '/images': 'Images',
  '/projects': 'Projects',
  '/settings': 'Settings',
}

/**
 * @param onOpenNav Opens the navigation drawer. Omitted on a screen that has
 *   no sidebar to open -- the handover page -- where the header stands alone
 *   and carries the lockup itself.
 */
export function AppHeader({ onOpenNav }: { onOpenNav?: () => void }) {
  const { pathname } = useLocation()
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)
  const [menuOpen, setMenuOpen] = useState(false)
  const menu = useRef<HTMLDivElement>(null)

  useEffect(() => setMenuOpen(false), [pathname])

  useEffect(() => {
    if (!menuOpen) return
    const onClick = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  // /chat/:id is still Chat.
  const title = TITLES[pathname] ?? (pathname.startsWith('/chat') ? 'Chat' : '')

  return (
    <header className="sticky top-0 z-20 flex h-[57px] shrink-0 items-center gap-2 border-b border-border bg-surface/85 px-3 backdrop-blur-[10px] sm:px-4">
      {onOpenNav && (
        <button
          type="button"
          onClick={onOpenNav}
          aria-label="Open navigation"
          className="rounded-lg p-1.5 text-content-muted transition hover:bg-surface-sunken hover:text-content lg:hidden"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Where there is a sidebar it carries the lockup from lg up, so the
          header only needs it while the sidebar is a drawer. With no sidebar
          at all, nothing else would show the mark. */}
      <span className={onOpenNav ? 'lg:hidden' : undefined}>
        <BrandLockup markClassName="h-7 w-7" textClassName="text-[16px]" />
      </span>

      <h1 className="hidden text-[15px] font-bold lg:block">{title}</h1>

      <div className="ml-auto flex items-center gap-1">
        <SuiteSwitcher compact align="right" />
        <ThemeToggle />

        <div ref={menu} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-haspopup="true"
            aria-label="Account"
            className={`flex items-center gap-1.5 rounded-lg py-1 pl-1 pr-1.5 transition ${
              menuOpen ? 'bg-surface-sunken' : 'hover:bg-surface-sunken'
            }`}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-[12px] font-bold text-accent-content">
              {(user?.name ?? user?.email ?? '?').charAt(0).toUpperCase()}
            </span>
            <ChevronDown
              size={14}
              className={`text-content-muted transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full z-40 mt-1.5 w-[248px] overflow-hidden rounded-xl border border-border bg-surface shadow-float">
              <div className="border-b border-border px-3.5 py-3">
                {user?.name && (
                  <p className="truncate text-[13.5px] font-bold text-content">{user.name}</p>
                )}
                <p className="truncate text-[12.5px] text-content-muted">{user?.email}</p>
              </div>
              <button
                type="button"
                onClick={() => void logout()}
                className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13.5px] font-semibold text-content transition hover:bg-surface-sunken"
              >
                <LogOut size={15} className="text-content-muted" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
